import React, {
  Children,
  useCallback,
  useEffect,
  useMemo,
  useRef,
} from 'react'
import Box from '@mui/material/Box'
import { useTheme } from '@mui/material/styles'
import type { SxProps, Theme } from '@mui/material/styles'
import useEmblaCarousel from 'embla-carousel-react'
import AutoHeight from 'embla-carousel-auto-height'
import type {
  EmblaCarouselType,
  EmblaOptionsType,
} from 'embla-carousel'

export interface SwipeableTabViewsProps {
  index: number
  onChangeIndex: (index: number) => void
  children: React.ReactNode
  /** When true, mouse drag is allowed (touch drag always works). Default false. */
  enableMouseEvents?: boolean
  /** Adapt viewport height to the active slide. Default true. */
  animateHeight?: boolean
  sx?: SxProps<Theme>
}

/**
 * Controlled swipeable tab panels (Embla + Auto Height).
 * Replaces unmaintained react-swipeable-views for Home / similar tab UIs.
 */
export const SwipeableTabViews: React.FC<SwipeableTabViewsProps> = ({
  index,
  onChangeIndex,
  children,
  enableMouseEvents = false,
  animateHeight = true,
  sx,
}) => {
  const theme = useTheme()
  const slides = useMemo(() => Children.toArray(children), [children])
  const onChangeIndexRef = useRef(onChangeIndex)
  onChangeIndexRef.current = onChangeIndex

  const plugins = useMemo(
    () => (animateHeight ? [AutoHeight()] : []),
    [animateHeight],
  )

  const options = useMemo<EmblaOptionsType>(
    () => ({
      align: 'start',
      containScroll: false,
      loop: false,
      duration: 25,
      direction: theme.direction === 'rtl' ? 'rtl' : 'ltr',
      watchDrag: (_api: EmblaCarouselType, event: Event) => {
        if (event.type.startsWith('mouse') || event.type === 'mousedown') {
          return enableMouseEvents
        }
        return true
      },
    }),
    [theme.direction, enableMouseEvents],
  )

  const [emblaRef, emblaApi] = useEmblaCarousel(options, plugins)

  const syncFromEmbla = useCallback((api: EmblaCarouselType) => {
    const selected = api.selectedScrollSnap()
    onChangeIndexRef.current(selected)
  }, [])

  useEffect(() => {
    if (!emblaApi) return
    const onSelect = () => syncFromEmbla(emblaApi)
    emblaApi.on('select', onSelect)
    return () => {
      emblaApi.off('select', onSelect)
    }
  }, [emblaApi, syncFromEmbla])

  useEffect(() => {
    if (!emblaApi) return
    if (emblaApi.selectedScrollSnap() !== index) {
      emblaApi.scrollTo(index)
    }
  }, [emblaApi, index])

  // Recalculate height when the active slide's content grows (async Overview widgets).
  // AutoHeight caches slideRects at init; reInit remounts the plugin with fresh heights.
  useEffect(() => {
    if (!emblaApi || !animateHeight) return

    const activeSlide = emblaApi.slideNodes()[emblaApi.selectedScrollSnap()]
    if (!activeSlide) return

    let raf = 0
    let lastHeight = 0
    const refreshHeight = () => {
      const nextHeight = activeSlide.getBoundingClientRect().height
      if (Math.abs(nextHeight - lastHeight) < 1) return
      lastHeight = nextHeight
      cancelAnimationFrame(raf)
      raf = requestAnimationFrame(() => {
        emblaApi.reInit()
      })
    }

    const observer = new ResizeObserver(refreshHeight)
    observer.observe(activeSlide)
    refreshHeight()

    return () => {
      cancelAnimationFrame(raf)
      observer.disconnect()
    }
  }, [emblaApi, animateHeight, index, slides.length])

  return (
    <Box
      ref={emblaRef}
      sx={[
        {
          width: '100%',
          maxWidth: '100%',
          overflow: 'hidden',
          '& .swipeable-tab-views__container': {
            display: 'flex',
            touchAction: 'pan-y pinch-zoom',
            alignItems: 'flex-start',
            // AutoHeight writes height on the container node
            transition: theme.transitions.create('height', {
              duration: theme.transitions.duration.shortest,
              easing: theme.transitions.easing.easeInOut,
            }),
          },
          '& .swipeable-tab-views__slide': {
            flex: '0 0 100%',
            minWidth: 0,
            width: '100%',
            maxWidth: '100%',
            padding: 0,
          },
        },
        ...(Array.isArray(sx) ? sx : sx ? [sx] : []),
      ]}
    >
      <Box className='swipeable-tab-views__container'>
        {slides.map((child, slideIndex) => (
          <Box
            key={slideIndex}
            className='swipeable-tab-views__slide'
            aria-hidden={slideIndex !== index}
          >
            {child}
          </Box>
        ))}
      </Box>
    </Box>
  )
}

export default SwipeableTabViews
